// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartLineContainer, { CartLineContainerStyles } from "@conklin/App/Components/Cart/CartLineContainer";
import Notify from "@conklin/App/Components/Notify";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import { xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import Tooltip, { TooltipPresentationProps } from "@conklin/MobiusOverrides/Tooltip";
import CartLineQuantity, { CartLineQuantityStyles } from "@conklin/Overrides/Widgets/Cart/CartLineQuantity";
import { ADD_TO_PROJECT } from "@conklin/XDL/constants";
import { parseBoolean } from "@conklin/XDL/Utilities";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getErrorMessages, isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { ProductSettingsModel, ProductSubscriptionDto, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ProductAvailability, { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDeliveryScheduleButton from "@insite/content-library/Components/ProductDeliveryScheduleButton";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useContext } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    showImage?: boolean;
    showProductTitle?: boolean;
    showBrand?: boolean;
    showPerUnitPrice?: boolean;
    showPriceLabel?: boolean;
    showAddToList?: boolean;
    showProductNumber?: boolean;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    hideAddToList?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    showVmiBinDetails?: boolean;
    // XNG-Changes
    isEditableQTY?: boolean;
}

const mapStateToProps = (state: CustomAppState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    // XNG-Changes
    isAuthenticated: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityAndExtendedUnitNetPriceGridItem?: GridItemProps;
    quantityAndExtendedUnitNetPriceContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    deliveryScheduleButton?: ButtonPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    cartLineNotesGridItem?: GridItemProps;
    cartLineNotes?: CartLineNotesStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    binInformationsGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
    // XNG-Changes
    cartLineContainer?: CartLineContainerStyles;
    extendedUnitNetPriceHeadingAndText?: SmallHeadingAndTextStyles;
    notesListWrapper?: InjectableCss;
    quoteMessageWrapper?: InjectableCss;
    quoteTotal?: TypographyPresentationProps;
    quoteText?: TypographyPresentationProps;
    quoteTooltip?: TooltipPresentationProps;
    invalidPriceHeadingAndText?: SmallHeadingAndTextStyles;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    null,
                ])}
        `,
    },
    cartLineInfoGridItem: { width: [8, 8, 8, 9, 9] },
    cartLineInfoContainer: { gap: 20 },
    productInfoGridItem: { width: [12, 12, 12, 7, 7] },
    productInfoContainer: { gap: 12 },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    productDescriptionGridItem: {
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersGridItem: { width: 12 },
    quantityAndExtendedUnitNetPriceGridItem: { width: 12 },
    quantityGridItem: { width: 6 },
    extendedUnitNetPriceGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-end;
        `,
    },
    extendedUnitNetPriceText: {
        weight: "bold",
    },
    vatLabelText: {
        size: 12,
    },
    subtotalWithoutVatText: {
        weight: "bold",
        css: css`
            margin-top: 5px;
        `,
    },
    cartLineErrorMessageGridItem: { width: 12 },
    cartLineErrorMessageText: { color: "danger" },
    productPriceAndAvailabilityGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            flex-direction: column;
        `,
    },
    addToListLink: {
        css: css`
            margin-top: 20px;
        `,
    },
    deliveryScheduleButton: {
        css: css`
            margin-top: 20px;
        `,
    },
    costCodeGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    costCodeLabelText: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    cartLineNotesGridItem: { width: 12 },
    removeCartLineGridItem: {
        css: css`
            justify-content: center;
        `,
        width: [2, 2, 2, 1, 1],
    },
    removeCartLineIcon: { src: XCircle },
    binInformationsGridItem: {
        width: 12,
    },
};

const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    promotions,
    productSettings,
    showInventoryAvailability,
    showLineNotes,
    showImage = true,
    showProductTitle = true,
    showBrand = true,
    showPerUnitPrice = true,
    showPriceLabel = true,
    showAddToList = true,
    showProductNumber = true,
    updateCartLine,
    removeCartLine,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    hideAddToList,
    enableVat,
    vatPriceDisplay,
    showPricing,
    showVmiBinDetails,
    // XNG-Changes
    isEditableQTY = true,
    isAuthenticated,
}) => {
    const toasterContext = useContext(ToasterContext);
    const currentMode = getCookie(NavigationModeCookieName);

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine({ cartLineId: cartLine.id });
            } else {
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                });
            }
        }
    };

    const costCodeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const costCode = event.currentTarget.value;
        if (costCode !== cartLine.costCode) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    costCode,
                },
                shouldNotLoadFullCart: true,
            });
        }
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    notes,
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC

                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        removeCartLine({ cartLineId: cartLine.id });
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC

                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const saveScheduleHandler = (updatedSubscription: ProductSubscriptionDto) => {
        updateCartLine({
            cartLine: {
                ...cartLine,
                properties: {
                    productSubscription: JSON.stringify(updatedSubscription),
                },
            },
        });
    };

    const errorMessages = getErrorMessages(cart, cartLine, showInventoryAvailability);

    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));
    const showPriceForQuotes = parseBoolean(cartLine.properties.showPriceQuoteRequired ?? false); // XNG-Changes: use CartLineContainer
    // XNG-Changes
    const quoteRequired =
        cartLine.quoteRequired ||
        (!isAuthenticated && parseBoolean(cartLine.properties.authRequiredForPurchase)) ||
        parseBoolean(cartLine.properties.isQuote);

    const displayLineNote =
        showLineNotes && !cartLine.isPromotionItem && cart.properties["isPunchout"] === undefined && cart.showLineNotes;
    return (
        <CartLineContainer
            extendedStyles={styles.cartLineContainer}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <CartLineContainer.Image>
                {showImage && <ProductImage product={cartLine} extendedStyles={styles.productImage} />}
            </CartLineContainer.Image>
            <CartLineContainer.Description>
                {showBrand && cartLine.brand && (
                    <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                )}
                {showProductTitle && (
                    <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                )}

                <StyledWrapper {...styles.productInfoContainer}>
                    <ProductPartNumbers
                        productNumber={cartLine.erpNumber}
                        customerProductNumber={cartLine.customerName}
                        manufacturerItem={cartLine.manufacturerItem}
                        extendedStyles={styles.productPartNumbers}
                        // XNG-Changes
                        showCustomerName={true}
                        showManufacturerItem={true}
                        {...{ sku: cartLine.properties?.sku || "" }}
                    />
                    {showVmiBinDetails && currentMode === "Vmi" && (
                        <ProductBinNumbers
                            locationId={cart.vmiLocationId}
                            binId={cartLine.vmiBinId}
                            isCondensed={false}
                            extendedStyles={styles.productBinNumbers}
                        />
                    )}
                    {/* XNG-Changes: add !quoteRequired */}
                    {!cart.cartNotPriced &&
                        (!quoteRequired || showPriceForQuotes) &&
                        !parseBoolean(cart.properties.erpSystemIsDown) && (
                            <ProductPrice
                                product={cartLine}
                                currencySymbol={cart.currencySymbol}
                                showSavings={true}
                                showSavingsAmount={productSettings.showSavingsAmount}
                                showSavingsPercent={productSettings.showSavingsPercent}
                                extendedStyles={styles.productPrice}
                                showLabel={showPriceLabel}
                                showUnitOfMeasure={showPerUnitPrice}
                            />
                        )}
                    {showPricing &&
                        promotions.map(promotion => (
                            <Typography {...styles.promotionNameText} key={promotion.id}>
                                {promotion.name}
                            </Typography>
                        ))}

                    {/* XNG-Changes */}
                    {parseBoolean(cart.properties.erpSystemIsDown) && (
                        <Typography as="p" {...styles.productPrice?.price?.errorText}>
                            {translate("Call For Pricing")}
                        </Typography>
                    )}
                </StyledWrapper>
                {!cartLine.isFixedConfiguration &&
                    cartLine.sectionOptions!.length > 0 &&
                    cartLine.sectionOptions!.map(option => (
                        <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                            {`${option.sectionName}:${option.optionName}`}
                        </Typography>
                    ))}
            </CartLineContainer.Description>
            <CartLineContainer.Inventory>
                {/* XNG-Changes */}
                {showInventoryAvailability && (
                    <ProductAvailability
                        productId={cartLine.productId!}
                        availability={cartLine.availability!}
                        unitOfMeasure={cartLine.unitOfMeasure}
                        trackInventory={cartLine.trackInventory}
                        failedToLoadInventory={cart.failedToGetRealTimeInventory}
                        extendedStyles={styles.productAvailability}
                    />
                )}
            </CartLineContainer.Inventory>
            <CartLineContainer.Qty>
                <CartLineQuantity
                    cart={cart}
                    // XNG-Changes
                    editable={isEditableQTY && !parseBoolean(cart.properties.erpSystemIsDown)}
                    onQtyOrderedChange={qtyOrderedChangeHandler}
                    extendedStyles={styles.quantity}
                    label={translate("QTY")}
                    showLabel={true}
                />
            </CartLineContainer.Qty>
            <CartLineContainer.Total>
                {/* /XNG-Changes */}
                {(!quoteRequired || showPriceForQuotes) &&
                    !parseBoolean(cart.properties.erpSystemIsDown) &&
                    !cart.cartNotPriced &&
                    cartLine.pricing &&
                    showPricing && (
                        <StyledWrapper {...styles.extendedUnitNetPriceGridItem}>
                            {/* XNG-Changes: use  SmallHeadingAndText */}
                            <SmallHeadingAndText
                                {...styles.extendedUnitNetPriceHeadingAndText}
                                data-test-selector="cartline_extendedUnitNetPrice"
                                heading={translate("Total")}
                                text={
                                    !cartLine.pricing.extendedUnitRegularPrice && !cartLine.pricing.extendedUnitNetPrice
                                        ? translate("Call For Pricing")
                                        : enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                          ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                          : cartLine.pricing.extendedUnitNetPriceDisplay
                                }
                            />

                            {enableVat && (
                                <>
                                    <Typography as="p" {...styles.vatLabelText}>
                                        {vatPriceDisplay === "DisplayWithVat" ||
                                        vatPriceDisplay === "DisplayWithAndWithoutVat"
                                            ? `${translate("Inc. VAT")} (${cartLine.pricing.vatRate}%)`
                                            : translate("Ex. VAT")}
                                    </Typography>
                                    {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                        <>
                                            <Typography {...styles.subtotalWithoutVatText}>
                                                {cartLine.pricing.extendedUnitNetPriceDisplay}
                                            </Typography>
                                            <Typography as="p" {...styles.vatLabelText}>
                                                {translate("Ex. VAT")}
                                            </Typography>
                                        </>
                                    )}
                                </>
                            )}
                        </StyledWrapper>
                    )}
                {/* XNG-Changes */}
                {parseBoolean(cart.properties.erpSystemIsDown) && (
                    <StyledWrapper {...styles.extendedUnitNetPriceGridItem}>
                        <SmallHeadingAndText
                            {...styles.extendedUnitNetPriceHeadingAndText}
                            data-test-selector="cartline_extendedUnitNetPrice"
                            heading={translate("Total")}
                            text={translate("Call For Pricing")}
                        />
                    </StyledWrapper>
                )}
                {/* XNG-Changes */}
                {quoteRequired && !showPriceForQuotes && !parseBoolean(cart.properties.erpSystemIsDown) && (
                    <StyledWrapper {...styles.quoteMessageWrapper}>
                        <Typography {...styles.quoteTotal}>{translate("Total")}</Typography>
                        <Typography {...styles.quoteText} data-test-selector="quoteRequiredText">
                            {translate("Quote")}
                        </Typography>
                        <Tooltip {...styles.quoteTooltip} text={siteMessage("Rfq_TooltipMessage").toString()} />
                    </StyledWrapper>
                )}
            </CartLineContainer.Total>
            <CartLineContainer.Notes>
                <StyledWrapper {...styles.notesListWrapper}>
                    {/* XNG-Changes: adming setting showLineNotes */}
                    {displayLineNote && (
                        <CartLineNotes
                            cart={cart}
                            editable={true}
                            onNotesChange={notesChangeHandler}
                            extendedStyles={styles.cartLineNotes}
                        />
                    )}

                    {showAddToList && !hideAddToList && cartLine.canAddToWishlist && !cartLine.isRestricted && (
                        <Link {...styles.addToListLink} {...{ displayLineNote }} onClick={addToListClickHandler}>
                            {ADD_TO_PROJECT}
                        </Link>
                    )}
                </StyledWrapper>
                {errorMessages.length > 0 && (
                    <StyledWrapper {...styles.cartLineErrorMessageGridItem}>
                        {errorMessages.map((message, index) => (
                            <Notify
                                type="danger"
                                sizeVariant="small"
                                showClose={false}
                                key={index}
                                data-test-selector="cartline_errorMessage"
                            >
                                {message}
                            </Notify>
                        ))}
                    </StyledWrapper>
                )}
                {cartLine.isSubscription && cartLine.productSubscription && (
                    <ProductDeliveryScheduleButton
                        subscription={
                            cartLine.properties.productSubscription
                                ? JSON.parse(cartLine.properties.productSubscription)
                                : cartLine.productSubscription
                        }
                        disabled={
                            !cart.canModifyOrder ||
                            cartLine.isPromotionItem ||
                            !cartLine.qtyOrdered ||
                            cartLine.qtyOrdered <= 0
                        }
                        onSave={saveScheduleHandler}
                        extendedStyles={styles.deliveryScheduleButton}
                    />
                )}
            </CartLineContainer.Notes>
            <CartLineContainer.Actions>
                {cart.showCostCode && !cartLine.isPromotionItem && (
                    <>
                        {cart.canEditCostCode ? (
                            <Select
                                value={cartLine.costCode}
                                onChange={costCodeChangeHandler}
                                label={cart.costCodeLabel}
                                aria-label={translate("Cost Code")}
                                {...styles.costCodeSelect}
                            >
                                {cart.costCodes?.map(costCode => (
                                    <option key={costCode.costCode} value={costCode.costCode}>
                                        {costCode.description}
                                    </option>
                                ))}
                            </Select>
                        ) : (
                            <Typography {...styles.costCodeText}>{cartLine.costCode}</Typography>
                        )}
                    </>
                )}
            </CartLineContainer.Actions>
            <CartLineContainer.Remove>
                {/* XNG-Changes */}
                {showRemoveAction && !parseBoolean(cart.properties.erpSystemIsDown) && (
                    <Clickable onClick={removeCartLineClickHandler} data-test-selector="cartline_removeLine">
                        <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                        <IconMemo {...styles.removeCartLineIcon} />
                    </Clickable>
                )}
            </CartLineContainer.Remove>
        </CartLineContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
